import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRegional, formatDate } from '@/lib/regional';
import { Download, ShieldCheck, Info } from 'lucide-react';

interface SysEntry {
  id: string;
  actor_email?: string;
  action: string;
  resource: string;
  target_id?: string;
  ip?: string;
  user_agent?: string;
  impersonated?: boolean;
  timestamp: string;
  diff?: Record<string, unknown>;
}
interface AccessEntry {
  id: string;
  broker_id: string;
  staff_id?: string;
  event_type: string;
  phone_used?: string;
  outcome: string;
  ip?: string;
  user_agent?: string;
  timestamp: string;
  notes: string;
}

// Reduce a user-agent string to a compact "Browser · OS" hint for the table.
const summariseUA = (ua?: string): string => {
  if (!ua) return '—';
  const s = ua;
  let browser = 'Unknown';
  if (/Edg\//.test(s)) browser = 'Edge';
  else if (/OPR\//.test(s)) browser = 'Opera';
  else if (/Chrome\//.test(s)) browser = 'Chrome';
  else if (/Firefox\//.test(s)) browser = 'Firefox';
  else if (/Safari\//.test(s) && !/Chrome\//.test(s)) browser = 'Safari';
  else if (/curl\//.test(s)) browser = 'curl';
  else if (/python-requests\//.test(s)) browser = 'python-requests';
  let os = 'Unknown';
  if (/Windows/.test(s)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(s)) os = 'macOS';
  else if (/Android/.test(s)) os = 'Android';
  else if (/iPhone|iPad|iOS/.test(s)) os = 'iOS';
  else if (/Linux/.test(s)) os = 'Linux';
  return `${browser} · ${os}`;
};

export const AuditPage: React.FC = () => {
  const [tab, setTab] = useState<'system' | 'access'>('system');
  const [sys, setSys] = useState<SysEntry[]>([]);
  const [acc, setAcc] = useState<AccessEntry[]>([]);
  const [filter, setFilter] = useState('');
  const { regional } = useRegional();

  useEffect(() => {
    api.get<SysEntry[]>('/audit/system').then((r) => setSys(r.data));
    api.get<AccessEntry[]>('/audit/broker-access').then((r) => setAcc(r.data));
  }, []);

  const filteredSys = useMemo(() => {
    if (!filter) return sys;
    const q = filter.toLowerCase();
    return sys.filter((e) =>
      [e.actor_email, e.action, e.resource, e.target_id, e.ip, e.user_agent]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [sys, filter]);

  const filteredAcc = useMemo(() => {
    if (!filter) return acc;
    const q = filter.toLowerCase();
    return acc.filter((e) =>
      [e.broker_id, e.staff_id, e.event_type, e.phone_used, e.outcome, e.ip, e.notes]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [acc, filter]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Audit log</h2>
          <p className="text-sm text-raptor-navy/60 font-body max-w-3xl">
            Every admin action + broker registration attempt is recorded to a tamper-evident,
            append-only log. Each entry captures the actor, the target resource, the
            <em> where-from</em> (IP address + browser / OS fingerprint), a full field-level
            diff of the change, and an impersonation flag when the session was performed on
            behalf of another user.
          </p>
        </div>
        <Button
          data-testid="export-access-log-btn"
          variant="outline"
          size="sm"
          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/exports/broker-access-log.xlsx`, '_blank')}
        >
          <Download size={16} /> Export access log
        </Button>
      </div>

      {/* SOC 2 compliance banner */}
      <Card data-testid="soc2-banner" className="border-raptor-blue/30 bg-raptor-blue/5">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="mt-0.5 text-raptor-blue">
            <ShieldCheck size={20} />
          </div>
          <div className="text-sm font-body text-raptor-navy/80 space-y-1">
            <div className="font-semibold text-raptor-navy flex items-center gap-2">
              SOC 2 Type II. Aligned <Badge variant="blue" className="text-[10px]">CC6.1 · CC7.2 · CC7.3</Badge>
            </div>
            <div>
              Audit records satisfy the SOC 2 Common Criteria evidentiary requirements for
              logical-access monitoring. Each entry records the <b>who</b> (actor id + email),
              <b> what</b> (action + resource + diff), <b>when</b> (ISO 8601 UTC timestamp),
              <b> where from</b> (client IP + user agent), and <b>how</b> (impersonation flag +
              session id). Retention is governed by the Audit &amp; Compliance settings
              (default 7 years) and log entries are append-only. The API exposes no update
              or delete endpoints.
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={tab === 'system' ? 'gradient' : 'outline'} onClick={() => setTab('system')} data-testid="audit-tab-system">
          System audit · {sys.length}
        </Button>
        <Button size="sm" variant={tab === 'access' ? 'gradient' : 'outline'} onClick={() => setTab('access')} data-testid="audit-tab-access">
          Broker access · {acc.length}
        </Button>
        <div className="flex-1 min-w-[220px] max-w-md">
          <Input
            data-testid="audit-filter"
            placeholder="Filter by actor, action, resource, IP…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-9"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-auto">
          {tab === 'system' && (
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-xs text-raptor-navy/70 uppercase">
                <tr>
                  <th className="py-2 px-3 text-left">When</th>
                  <th className="py-2 px-3 text-left">Actor</th>
                  <th className="py-2 px-3 text-left">Action</th>
                  <th className="py-2 px-3 text-left">Resource</th>
                  <th className="py-2 px-3 text-left">Target</th>
                  <th className="py-2 px-3 text-left" title="Origin. SOC 2 CC7.2 evidentiary field">Where from</th>
                </tr>
              </thead>
              <tbody>
                {filteredSys.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-raptor-navy/50">
                      {sys.length === 0 ? 'No audit entries yet.' : 'No entries match the filter.'}
                    </td>
                  </tr>
                )}
                {filteredSys.map((e) => (
                  <tr key={e.id} className="border-t border-border/60 align-top" data-testid={`audit-row-${e.id}`}>
                    <td className="py-2 px-3 font-tabular text-xs whitespace-nowrap">{formatDate(e.timestamp, regional)}</td>
                    <td className="py-2 px-3">
                      {e.actor_email ?? '—'}
                      {e.impersonated && <Badge variant="warning" className="ml-2">impersonated</Badge>}
                    </td>
                    <td className="py-2 px-3"><Badge variant="blue">{e.action}</Badge></td>
                    <td className="py-2 px-3">{e.resource}</td>
                    <td className="py-2 px-3 font-tabular text-xs">{e.target_id ?? '—'}</td>
                    <td className="py-2 px-3 text-xs">
                      <div className="font-tabular text-raptor-navy" data-testid={`audit-ip-${e.id}`}>{e.ip || '—'}</div>
                      <div className="text-raptor-navy/60" title={e.user_agent || ''}>{summariseUA(e.user_agent)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === 'access' && (
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-xs text-raptor-navy/70 uppercase">
                <tr>
                  <th className="py-2 px-3 text-left">When</th>
                  <th className="py-2 px-3 text-left">Broker</th>
                  <th className="py-2 px-3 text-left">Event</th>
                  <th className="py-2 px-3 text-left">Phone</th>
                  <th className="py-2 px-3 text-left">Outcome</th>
                  <th className="py-2 px-3 text-left">Where from</th>
                  <th className="py-2 px-3 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredAcc.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-raptor-navy/50">
                      {acc.length === 0 ? 'No access events yet.' : 'No entries match the filter.'}
                    </td>
                  </tr>
                )}
                {filteredAcc.map((e) => {
                  const good = /Verified|matched|granted|complete/i.test(e.outcome);
                  return (
                    <tr key={e.id} className="border-t border-border/60 align-top">
                      <td className="py-2 px-3 font-tabular text-xs whitespace-nowrap">{formatDate(e.timestamp, regional)}</td>
                      <td className="py-2 px-3 font-tabular text-xs">{e.broker_id}</td>
                      <td className="py-2 px-3"><Badge variant={good ? 'success' : 'muted'}>{e.event_type}</Badge></td>
                      <td className="py-2 px-3 font-tabular text-xs">{e.phone_used ?? '—'}</td>
                      <td className="py-2 px-3">
                        {good ? <Badge variant="success">{e.outcome}</Badge> : <Badge variant="destructive">{e.outcome}</Badge>}
                      </td>
                      <td className="py-2 px-3 text-xs">
                        <div className="font-tabular">{e.ip || '—'}</div>
                        <div className="text-raptor-navy/60" title={e.user_agent || ''}>{summariseUA(e.user_agent)}</div>
                      </td>
                      <td className="py-2 px-3 text-xs text-raptor-navy/70">{e.notes}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-raptor-navy/50 font-body flex items-start gap-2 max-w-3xl">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          &quot;Where from&quot; combines the client IP address (honouring standard
          reverse-proxy headers <code>X-Forwarded-For</code> / <code>X-Real-IP</code>) with a
          summarised User-Agent (browser · OS). Hover the browser·OS text to see the raw
          User-Agent string. Legacy entries recorded before this field was populated will show
          &quot;—&quot;.
        </span>
      </div>
    </div>
  );
};
